import { Box } from '@chakra-ui/react';
import { Code } from 'constants/keyboard';
import { addNewWidget, useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectActiveWidgetMenu } from 'modules/Editor/store/editorSettingsSelector';
import { setActiveWidgetMenu } from 'modules/Editor/store/editorSettingsSlice';
import { useUserMedia } from 'modules/User';
import { SidePanel } from 'modules/common/components/SidePanel';
import { SidePanelHeader } from 'modules/common/components/SidePanel';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WIDGET_MENU_OPTIONS } from 'types/WidgetMenu.types';
import { NewWidget } from 'widgets/Widget.types';

import { SIDEBAR_ITEMS, SIDEMENU_CLOSE_BTN_ID } from '../WidgetMenu.config';
import { BrandKitMenu } from './BrandKitMenu';
import { ChartsWidgetMenu } from './ChartsWidgetMenu/ChartsWidgetMenu';
import { IconWidgetMenu } from './IconWidgetMenu';
import { MapWidgetMenu } from './MapWidgetMenu/MapWidgetMenu';
import { PhotoWidgetMenu } from './PhotoWidgetMenu';
import { PhotoWidgetMenuCategory } from './PhotoWidgetMenu/PhotoWidgetCategory';
import { QRCodeMenu } from './QRCodeMenu';
import { ReplaceMenu } from './ReplaceMenu/ReplaceMenu';
import { ShapesMenu } from './ShapesMenu';
import { SmartWidgetMenu } from './SmartWidgetMenu';
import { TemplatesMenu } from './TemplatesMenu';
import { TextWidgetMenu } from './TextWidgetMenu';

const TITLE_ID = 'sidemenu-title';

export const SideMenu = () => {
  const activeWidgetMenu = useAppSelector(selectActiveWidgetMenu);
  const dispatch = useAppDispatch();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false });
  const [isNavFocusEnabled, setIsNavFocusEnabled] = useState<boolean>(true);

  const isOpen = useMemo(() => activeWidgetMenu !== WIDGET_MENU_OPTIONS.NONE, [activeWidgetMenu]);

  const activeItemTitle = useMemo(() => {
    if (activeWidgetMenu === WIDGET_MENU_OPTIONS.REPLACE) return t('sideMenu.replace');
    const activeItem = SIDEBAR_ITEMS.find((item) => item.id === activeWidgetMenu);

    return activeItem?.id ? t(`sideBar.${activeItem.id.replaceAll('-', '_')}.title` as keyof typeof t) : '';
  }, [activeWidgetMenu, t]);

  useUserMedia({}); // preload user upload images, no need to show error message until upload menu is opened

  const handleClose = () => dispatch(setActiveWidgetMenu(WIDGET_MENU_OPTIONS.NONE));

  useLayoutEffect(() => {
    // Wait for menu to render before focusing on close button
    if (activeWidgetMenu) {
      if (
        activeWidgetMenu === WIDGET_MENU_OPTIONS.ICONS ||
        activeWidgetMenu === WIDGET_MENU_OPTIONS.PHOTOS ||
        activeWidgetMenu === WIDGET_MENU_OPTIONS.MAPS
      ) {
        // Skipping these widget menus since they have their own focus logic with focusOnMenuOpen
        // WIDGET_MENU_OPTIONS.TEMPLATES is not included as the focus would show the suggestion list on default and block the templates.
        return;
      }

      closeButtonRef.current?.focus();
    }
  }, [activeWidgetMenu]);

  // focus events cannot discern between mouse and keyboard event types
  // for mousedown we need to manually disable focus navigation style effects
  const onMouseDown = () => {
    setIsNavFocusEnabled(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.code === Code.Escape) handleClose();

    // re-enable focus navigation style effects when tab is pressed
    if (e.code === Code.Tab) {
      setIsNavFocusEnabled(true);
    }
  };

  const onClickAddWidget = (widget: NewWidget | NewWidget[]) => dispatch(addNewWidget(widget));

  return (
    <SidePanel
      aria-labelledby={TITLE_ID}
      zIndex='var(--vg-zIndices-sideMenu)'
      onKeyDown={onKeyDown}
      onMouseDown={onMouseDown}
      isOpen={isOpen}
      placement={'left'}
    >
      <SidePanelHeader
        title={activeItemTitle}
        titleId={TITLE_ID}
        onClose={handleClose}
        ref={closeButtonRef}
        closeButtonAriaLabel={t('sideMenu.panelHeader.closeButtonAriaLabel')}
        closeButtonTestId={SIDEMENU_CLOSE_BTN_ID}
      />
      <Box flex='1' overflow='hidden'>
        {activeWidgetMenu === WIDGET_MENU_OPTIONS.BRAND_KIT && <BrandKitMenu onItemClick={onClickAddWidget} />}
        {<TemplatesMenu isOpen={activeWidgetMenu === WIDGET_MENU_OPTIONS.TEMPLATES} />}
        {activeWidgetMenu === WIDGET_MENU_OPTIONS.REPLACE && <ReplaceMenu allowNavFocus={isNavFocusEnabled} />}
        {activeWidgetMenu === WIDGET_MENU_OPTIONS.TEXT && <TextWidgetMenu />}
        {activeWidgetMenu === WIDGET_MENU_OPTIONS.SMART && <SmartWidgetMenu />}
        {activeWidgetMenu === WIDGET_MENU_OPTIONS.SHAPES && <ShapesMenu allowNavFocus={isNavFocusEnabled} />}
        {activeWidgetMenu === WIDGET_MENU_OPTIONS.CHARTS && <ChartsWidgetMenu />}
        <IconWidgetMenu
          onClickIconWidget={onClickAddWidget}
          isIconWidgetMenuActive={activeWidgetMenu === WIDGET_MENU_OPTIONS.ICONS}
          allowNavFocus={isNavFocusEnabled}
        />
        <PhotoWidgetMenu
          onItemClick={onClickAddWidget}
          isMenuActive={activeWidgetMenu === WIDGET_MENU_OPTIONS.PHOTOS}
        />
        <PhotoWidgetMenuCategory
          onItemClick={onClickAddWidget}
          isMenuActive={activeWidgetMenu === WIDGET_MENU_OPTIONS.UPLOAD}
        />
        {activeWidgetMenu === WIDGET_MENU_OPTIONS.QR_CODE && <QRCodeMenu />}
        {activeWidgetMenu === WIDGET_MENU_OPTIONS.MAPS && <MapWidgetMenu />}
      </Box>
    </SidePanel>
  );
};
